// Payment read-model for the booking sheet (DC62; design Others.dc.html vSheet
// payHeadline / moneyRows / timeline, customer gap §A.3 B3). Pure over the contract
// Booking: every amount is the server's (DB-14a) — nothing is priced here, only
// arranged. A rejected proof is the stored `rejected` status plus the proof's
// `reject_reason_key` (DR-46) and free-text note.
import type { Booking, PaymentProof } from '../../mock/seeds/schema-types';

export type PayHeadline =
  | { kind: 'unaccepted'; amount: number }
  | { kind: 'due'; amount: number }
  | { kind: 'venue'; amount: number }
  | { kind: 'advance'; paid: number; due: number }
  | { kind: 'paid'; amount: number };

export type MoneyRowKind = 'total' | 'discount' | 'paid' | 'counter' | 'awaiting';
export interface MoneyRow {
  kind: MoneyRowKind;
  amount: number;
}

export type ProofStep = 'sent' | 'checking' | 'accepted' | 'rejected';
export interface ProofStepRow {
  step: ProofStep;
  done: boolean;
  current: boolean;
  at?: string;
}

/** The proof that speaks for the booking now: the newest non-superseded one. */
export function latestProof(b: Booking): PaymentProof | undefined {
  const live = (b.proofs ?? []).filter((p) => p.status !== 'superseded');
  const pool = live.length ? live : (b.proofs ?? []);
  return [...pool].sort((a, c) => c.created_at.localeCompare(a.created_at))[0];
}

/** Paid so far: the whole total once a visit is completed and settled, else the advance. */
export function paidMinor(b: Booking): number {
  if (b.status === 'completed' && b.settled_at) return b.total_minor;
  return b.advance_paid_minor ?? 0;
}

export function isProofRejected(b: Booking): boolean {
  return b.status === 'rejected' || latestProof(b)?.status === 'rejected';
}

/** A proof has been sent (or rejected) and no money is accepted yet. */
export function isUnaccepted(b: Booking): boolean {
  if (paidMinor(b) > 0) return false;
  const p = latestProof(b);
  return isProofRejected(b) || p?.status === 'submitted';
}

export function payHeadline(b: Booking): PayHeadline {
  const paid = paidMinor(b);
  const total = b.total_minor;
  if (isUnaccepted(b)) {
    const p = latestProof(b);
    return { kind: 'unaccepted', amount: p?.amount_minor ?? b.advance_due_minor ?? total };
  }
  if (paid <= 0) {
    // An advance is owed and nothing has been sent yet.
    if (b.pay_method !== 'venue' && (b.advance_due_minor ?? 0) > 0 && b.status === 'pending') {
      return { kind: 'due', amount: b.advance_due_minor ?? 0 };
    }
    return { kind: 'venue', amount: total };
  }
  const due = Math.max(0, total - paid);
  if (due > 0) return { kind: 'advance', paid, due };
  return { kind: 'paid', amount: paid };
}

export function moneyRows(b: Booking): MoneyRow[] {
  const rows: MoneyRow[] = [{ kind: 'total', amount: b.total_minor }];
  if ((b.discount_minor ?? 0) > 0) rows.push({ kind: 'discount', amount: b.discount_minor ?? 0 });
  const paid = paidMinor(b);
  if (paid > 0) {
    rows.push({ kind: 'paid', amount: paid });
    const due = Math.max(0, b.total_minor - paid);
    if (due > 0) rows.push({ kind: 'counter', amount: due });
  } else if (isUnaccepted(b)) {
    rows.push({
      kind: 'awaiting',
      amount: latestProof(b)?.amount_minor ?? b.advance_due_minor ?? b.total_minor,
    });
  } else {
    rows.push({ kind: 'counter', amount: b.total_minor });
  }
  return rows;
}

/** Sent → Being checked → Accepted | Rejected, from the proof's own timestamps. */
export function proofSteps(p: PaymentProof): ProofStepRow[] {
  const decided = p.status === 'verified' || p.status === 'rejected';
  const last: ProofStep = p.status === 'rejected' ? 'rejected' : 'accepted';
  return [
    { step: 'sent', done: true, current: false, at: p.created_at },
    { step: 'checking', done: true, current: !decided, at: p.created_at },
    { step: last, done: decided, current: decided, at: decided ? p.reviewed_at : undefined },
  ];
}

/** The payment method to show: the proof's, else the instructions' method key. */
export function paymentMethod(b: Booking): string | undefined {
  const fromProof = latestProof(b)?.method;
  if (fromProof) return fromProof;
  const m = (b.payment_instructions as { method?: unknown } | undefined)?.method;
  return typeof m === 'string' ? m : undefined;
}

/**
 * The amount a proof states it pays (submitProof `amount_minor`, read by the merchant's
 * "amount matches" check, API-96). The design has no amount field on the proof step,
 * so it is the server's advance due — or the total when no advance is set.
 */
export function proofAmountMinor(b: Booking): number {
  const due = b.advance_due_minor ?? 0;
  return due > 0 ? due : b.total_minor;
}

/** A receipt exists once money was actually paid (API-76, DR-42). */
export function canDownloadReceipt(b: Booking): boolean {
  return paidMinor(b) > 0;
}
